import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

const roleBadge = {
  admin: "bg-[#002FA7] text-white",
  manager: "bg-[#0A0A0B] text-white",
  staff: "bg-slate-200 text-slate-900",
};

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [roleTestResult, setRoleTestResult] = useState({
    adminOnly: null,
    managerArea: null,
  });
  const [testing, setTesting] = useState(false);

  const onLogout = async () => {
    await logout();
    toast.success("Signed out.");
    navigate("/login", { replace: true });
  };

  const testRoleRoutes = async () => {
    setTesting(true);
    const results = { adminOnly: null, managerArea: null };
    try {
      const r1 = await api.get("/auth/admin-only");
      results.adminOnly = { ok: true, message: r1.data.message };
    } catch (e) {
      results.adminOnly = { ok: false, message: formatApiError(e) };
    }
    try {
      const r2 = await api.get("/auth/manager-area");
      results.managerArea = { ok: true, message: r2.data.message };
    } catch (e) {
      results.managerArea = { ok: false, message: formatApiError(e) };
    }
    setRoleTestResult(results);
    setTesting(false);
  };

  useEffect(() => {
    testRoleRoutes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!user) return null;

  return (
    <div
      data-testid="dashboard-page"
      className="min-h-screen bg-[#F9F9F6] text-slate-900"
    >
      {/* Top bar */}
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 bg-[#0A0A0B]" />
            <span className="text-xs uppercase tracking-[0.3em] font-semibold">
              Sprint&nbsp;02 · Console
            </span>
          </div>
          <button
            data-testid="dashboard-logout-button"
            onClick={onLogout}
            className="h-10 px-5 border border-slate-300 text-sm font-medium rounded-sm hover:bg-slate-50 transition-colors"
          >
            Sign out
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 sm:px-10 py-16">
        <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#002FA7] mb-4">
          Authenticated
        </p>
        <h1
          data-testid="dashboard-welcome-heading"
          style={{ fontFamily: "'Outfit', sans-serif" }}
          className="text-4xl sm:text-5xl font-semibold tracking-tight leading-none"
        >
          Welcome, {user.name}.
        </h1>
        <p className="mt-4 text-slate-600 max-w-2xl leading-relaxed">
          Sprint 2 delivers the identity layer. Sprint 3 adds the Inventory
          module — products (price-free master), warehouses, categories, and a
          per-warehouse stock ledger. Sales, Purchase, GST, Ledger and Reports
          arrive in later sprints.
        </p>

        {/* Modules */}
        <section className="mt-10 grid md:grid-cols-3 gap-0 border border-slate-200 bg-white">
          <button
            data-testid="dashboard-open-inventory"
            onClick={() => navigate("/inventory")}
            className="p-6 border-r border-slate-200 last:border-r-0 text-left hover:bg-slate-50 transition-colors"
          >
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#002FA7] mb-3">
              Sprint 3 · Live
            </div>
            <div className="text-xl font-semibold tracking-tight">Inventory</div>
            <div className="text-xs text-slate-500 mt-1">
              Products, warehouses, stock ledger
            </div>
          </button>
          <div className="p-6 border-r border-slate-200 last:border-r-0">
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-400 mb-3">
              Upcoming
            </div>
            <div className="text-xl font-semibold tracking-tight text-slate-400">Sales</div>
            <div className="text-xs text-slate-500 mt-1">
              Customers, orders, invoices
            </div>
          </div>
          <div className="p-6">
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-400 mb-3">
              Upcoming
            </div>
            <div className="text-xl font-semibold tracking-tight text-slate-400">Purchase</div>
            <div className="text-xs text-slate-500 mt-1">
              Vendors, POs, GRN
            </div>
          </div>
        </section>

        {/* Identity card */}
        <section className="mt-14 grid md:grid-cols-3 gap-0 border border-slate-200 bg-white">
          <IdBlock label="User ID" value={<span data-testid="dashboard-user-id" className="break-all">{user.id}</span>} mono />
          <IdBlock
            label="Identifier"
            value={
              <span data-testid="dashboard-user-identifier">
                {user.email || user.phone}
              </span>
            }
          />
          <IdBlock
            label="Role"
            value={
              <span
                data-testid="dashboard-user-role"
                className={`inline-block px-3 py-1 text-xs uppercase tracking-[0.2em] font-semibold ${roleBadge[user.role] || roleBadge.staff}`}
              >
                {user.role}
              </span>
            }
          />
        </section>

        {/* Role-check panel */}
        <section className="mt-16">
          <div className="flex items-center justify-between mb-6">
            <h2
              style={{ fontFamily: "'Outfit', sans-serif" }}
              className="text-2xl font-medium tracking-tight"
            >
              Protected routes
            </h2>
            <button
              data-testid="dashboard-recheck-button"
              onClick={testRoleRoutes}
              disabled={testing}
              className="h-10 px-5 border border-slate-300 text-sm font-medium rounded-sm hover:bg-slate-50 transition-colors disabled:opacity-60"
            >
              {testing ? "Checking…" : "Re-check"}
            </button>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <RouteResultCard
              testid="route-admin-only"
              label="GET /api/auth/admin-only"
              required="admin"
              result={roleTestResult.adminOnly}
            />
            <RouteResultCard
              testid="route-manager-area"
              label="GET /api/auth/manager-area"
              required="admin, manager"
              result={roleTestResult.managerArea}
            />
          </div>
        </section>

        <section className="mt-16 text-xs uppercase tracking-[0.25em] text-slate-500">
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/docs`}
            target="_blank"
            rel="noreferrer noopener"
            data-testid="dashboard-swagger-link"
            className="hover:text-[#002FA7] transition-colors"
          >
            → Open Swagger UI
          </a>
        </section>
      </main>
    </div>
  );
}

function IdBlock({ label, value, mono }) {
  return (
    <div className="p-6 border-r border-b border-slate-200 last:border-r-0 md:last:border-r-0">
      <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-3">
        {label}
      </div>
      <div className={`text-sm ${mono ? "font-mono" : "font-medium"} text-slate-900`}>
        {value}
      </div>
    </div>
  );
}

function RouteResultCard({ testid, label, required, result }) {
  const pending = result === null;
  const ok = result?.ok;
  return (
    <div
      data-testid={testid}
      className={`border p-6 bg-white ${
        pending
          ? "border-slate-200"
          : ok
          ? "border-[#039855]/40"
          : "border-[#D92D20]/40"
      }`}
    >
      <div className="flex items-center justify-between">
        <code className="text-xs font-mono text-slate-700">{label}</code>
        <span
          data-testid={`${testid}-status`}
          className={`text-xs uppercase tracking-[0.2em] font-semibold ${
            pending
              ? "text-slate-500"
              : ok
              ? "text-[#039855]"
              : "text-[#D92D20]"
          }`}
        >
          {pending ? "…" : ok ? "Allowed" : "Denied"}
        </span>
      </div>
      <div className="mt-3 text-xs uppercase tracking-[0.2em] text-slate-500">
        Required role: {required}
      </div>
      <p
        data-testid={`${testid}-message`}
        className="mt-4 text-sm text-slate-700 leading-relaxed break-words"
      >
        {pending ? "Checking…" : result.message}
      </p>
    </div>
  );
}
